import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  DisplayColumnDef,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable
} from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { USER_ROLE } from 'khufu-legos';
import { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Dropdown, Form, InputGroup, Row, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { getOrders } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import getStatusColor from '../../helpers/getStatusColor';
import { RootState } from '../../store/redux/store';
import { Order } from '../../types';
// import { formatDateTime } from '../../utils/getTimeFormat';
import { formatDateShort } from '../../utils/formatShortDate';
import { formatTime } from '../../utils/formatTime';
import AnalyticsHeader from '../../widgets/Header';
import NumberCell from '../../widgets/NumberCell';
import CreateInvoice from '../Invoice/CreateInvoice/CreateInvoice';
import CancelOrderModal from './CreateOrder/CanceOrderModal';

type ORDER_STATUS = 'open' | 'cancelled' | 'partiallyFulfilled' | 'fulfilled';

interface OrdersTableProps {
  query?: Record<'status', ORDER_STATUS>;
}

export const ORDERS_QUERY_KEY = ['orders'];

const CreateOrderButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> Create Order </Button>;
};

const globalFilterFn: FilterFn<Order> = (row, columnId, filterValue) => {
  const orderNumber = row.original.orderNumber?.toString().toLowerCase();
  const customerName = row.original.customer?.name?.toLowerCase() ?? '';
  const createdByName = row.original.createdBy?.fullName?.toLowerCase() ?? '';
  const searchValue = filterValue.toLowerCase();
  return orderNumber.includes(searchValue) || customerName.includes(searchValue) || createdByName.includes(searchValue);
};

const columnHelper = createColumnHelper<Order>();

const columns = [
  columnHelper.accessor('orderNumber', {
    header: 'Number',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('customer.name', {
    header: 'Customer',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => (
      <Badge bg={`${getStatusColor(info.getValue())}-soft`}>
        {' '}
        <div className="fw-bold"> {info.getValue()} </div>{' '}
      </Badge>
    ),
    footer: info => info.column.id
  }),
  columnHelper.accessor('type', {
    header: 'Order Type',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  // columnHelper.accessor('discount', {
  //   header: 'Discount',
  //   cell: info => info.getValue(),
  //   footer: info => info.column.id
  // }),
  columnHelper.accessor('total', {
    header: 'Total',
    size: 200,
    cell: info => <NumberCell text={currencyFormatter(info.getValue())} />,
    footer: info => info.column.id
  }),
  columnHelper.accessor('balance', {
    header: 'Balance',
    size: 200,
    cell: info => <NumberCell text={currencyFormatter(info.getValue())} />,
    footer: info => info.column.id
  }),
  columnHelper.accessor('productsCases', {
    header: 'Cases',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created At',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created Time',
    id: 'createdTime',
    cell: info => formatTime(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdBy.fullName', {
    header: 'Created By',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: () => {
      return (
        <Dropdown>
          <div role="button" style={{ display: 'inline-block' }}>
            <Dropdown.Toggle as="span" className="dropdown-ellipses">
              <FeatherIcon icon="more-vertical" size="17" />
            </Dropdown.Toggle>
          </div>
        </Dropdown>
      );
    },
    footer: info => info.column.id ?? ''
  })
];

const limit = 100;

const OrdersTable = ({ query }: OrdersTableProps) => {
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const compositeQueryKey = query
    ? [...ORDERS_QUERY_KEY, { ...query, page, limit }]
    : [ORDERS_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getOrders({ ...query, page, limit })
  });

  useEffect(() => {
    setPage(1);
  }, [query]);

  const orders = data?.results ?? [];
  const { count } = data ?? {};

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  const navigate = useNavigate();

  const handleShowModal = (order: Order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setSelectedOrder(null);
    setShowModal(false);
  };

  let noOrdersMessage;
  switch (query?.status) {
    case 'open':
      noOrdersMessage = orders && orders.length === 0 && <EmptyTableState message="No open orders yet." />;
      break;
    case 'cancelled':
      noOrdersMessage = orders && orders.length === 0 && <EmptyTableState message="No cancelled orders yet." />;
      break;
    case 'partiallyFulfilled':
      noOrdersMessage = orders && orders.length === 0 && <EmptyTableState message="No processing orders yet." />;
      break;
    case 'fulfilled':
      noOrdersMessage = orders && orders.length === 0 && <EmptyTableState message="No fulfilled orders yet." />;
      break;
    default:
      noOrdersMessage = null;
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <AnalyticsHeader
        title="Orders"
        tabs={tabRoutes}
        headerButton={
          <CreateOrderButton
            onClick={() => {
              navigate('/create-order');
            }}
          />
        }
      />

      <div className="main-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12}>
              {isLoading ? (
                <Loader />
              ) : noOrdersMessage ? (
                noOrdersMessage
              ) : orders && orders.length > 0 ? (
                <>
                  <TableList orders={orders} columns={columns} handleShowModal={orderId => handleShowModal(orderId)} />
                  <PaginationControls
                    currentPage={page}
                    totalPages={count ?? 0}
                    onPageChange={handlePageChange}
                    nextPage={nextPage ?? null}
                    prevPage={prevPage ?? null}
                  />
                </>
              ) : (
                <EmptyTableState message="No orders yet." />
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <CancelOrderModal show={showModal} onHide={handleCloseModal} order={selectedOrder} />
    </>
  );
};

const tabRoutes = [
  {
    title: 'All',
    path: 'all',
    tabLink: '/orders/all',
    element: <OrdersTable />
  },
  {
    title: 'Open',
    path: 'open',
    tabLink: '/orders/open',
    element: <OrdersTable query={{ status: 'open' }} />
  },
  {
    title: 'Processing',
    path: 'partiallyFulfilled',
    tabLink: '/orders/partiallyFulfilled',
    element: <OrdersTable query={{ status: 'partiallyFulfilled' }} />
  },
  {
    title: 'Fulfilled',
    path: 'fulfilled',
    tabLink: '/orders/fulfilled',
    element: <OrdersTable query={{ status: 'fulfilled' }} />
  },
  {
    title: 'Cancelled',
    path: 'cancelled',
    tabLink: '/orders/cancelled',
    element: <OrdersTable query={{ status: 'cancelled' }} />
  }
];

const Orders = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { data: orderData } = useQuery({
    queryKey: ['orderDetails', orderId],
    queryFn: () => getOrders({ _id: orderId }),
    enabled: !!orderId
  });

  const isCompleteInvoice = orderData?.results?.[0]?.balance === 0;

  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
          {!isCompleteInvoice && <Route path="create" element={<CreateInvoice />} />}
        </Routes>
      </div>
    </>
  );
};

export default Orders;

interface TableListProps {
  orders: Order[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (AccessorKeyColumnDef<Order, any> | DisplayColumnDef<Order, any>)[];
  handleShowModal: (order: Order) => void;
}

const TableList = ({ orders, columns, handleShowModal }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const userDetails = useSelector((state: RootState) => state.user.currentUser);
  const isAdmin = userDetails?.role === USER_ROLE.ADMIN;

  const tableInstance = useReactTable({
    data: orders!,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter
  });
  return (
    <Card>
      <Card.Header>
        <InputGroup className="input-group-merge input-group-flush input-group-reverse">
          <Form.Control
            type="search"
            placeholder="Search by Order Number, Customer Name, or Created By"
            onChange={e => setGlobalFilter(e.target.value ? e.target.value : undefined)}
          />
          <InputGroup.Text>
            <FeatherIcon icon="search" size="1em" />
          </InputGroup.Text>
        </InputGroup>
      </Card.Header>
      <div
        style={{
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 300px)',
          position: 'relative'
        }}
      >
        <Table size="sm" className="card-table table-nowrap" hover>
          <thead className="position-sticky top-0 z-2">
            {tableInstance.getHeaderGroups().map(headerGroup => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    return (
                      <th colSpan={header.colSpan} key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {tableInstance.getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {cell.column.id === 'actions' ? (
                          <Dropdown>
                            <div role="button" style={{ display: 'inline-block' }}>
                              <Dropdown.Toggle as="span" className="dropdown-ellipses">
                                <FeatherIcon icon="more-vertical" size="17" />
                              </Dropdown.Toggle>
                            </div>
                            {row.original.status !== 'fulfilled' && (
                              <DropdownMenuPortal>
                                <Dropdown.Menu>
                                  {row.original.status !== 'cancelled' && row.original.balance !== 0 && (
                                    <Dropdown.Item onClick={() => navigate(`/create-invoice/${row.original._id}`)}>
                                      Generate Invoice
                                    </Dropdown.Item>
                                  )}
                                  {row.original.status === 'open' && isAdmin ? (
                                    <Dropdown.Item
                                      style={{ color: 'red' }}
                                      onClick={() => handleShowModal(row.original)}
                                    >
                                      {row.original.hasReturnOrder ? 'Cancel Returned Order' : 'Cancel Order'}
                                    </Dropdown.Item>
                                  ) : !row.original.hasReturnOrder ? (
                                    <Dropdown.Item
                                      style={{ color: 'red' }}
                                      onClick={() => handleShowModal(row.original)}
                                    >
                                      {'Cancel Order'}
                                    </Dropdown.Item>
                                  ) : (
                                    <></>
                                  )}
                                </Dropdown.Menu>
                              </DropdownMenuPortal>
                            )}
                          </Dropdown>
                        ) : (
                          <Link to={`/order/${row.original._id}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Link>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

const DropdownMenuPortal = ({ children }: { children: React.ReactNode }) => {
  const dropdownRoot = document.getElementById('dropdown-root');
  return createPortal(children, dropdownRoot!);
};
